import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type PlanInfo } from "../../lib/api";

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Give Stripe's webhook a moment to land before we fetch plan state.
    // In most cases it arrives within a second; 2 s is a safe buffer.
    const timer = setTimeout(() => {
      api.billing
        .plans()
        .then((r) => setCurrent(r.current))
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const planLabel =
    current?.plan && current.plan !== "free"
      ? current.plan.charAt(0).toUpperCase() + current.plan.slice(1)
      : null;

  const isTrialing = current?.status === "trialing";

  const periodEndDate = current?.periodEnd
    ? new Date(current.periodEnd).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        {/* Success icon */}
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        {loading ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Confirming your subscription…
            </h1>
            <p className="text-sm text-gray-500">Just a moment.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isTrialing ? "Your free trial has started!" : "You're all set!"}
            </h1>

            {planLabel && (
              <p className="text-sm text-gray-600 mb-1">
                You're now on the{" "}
                <span className="font-semibold text-gray-900">{planLabel}</span>{" "}
                plan.
              </p>
            )}

            {isTrialing && periodEndDate && (
              <p className="text-sm text-gray-500">
                Your 14-day trial runs until{" "}
                <span className="font-medium text-gray-700">
                  {periodEndDate}
                </span>
                . You won't be charged until then.
              </p>
            )}

            {!isTrialing && periodEndDate && (
              <p className="text-sm text-gray-500">
                Next billing date:{" "}
                <span className="font-medium text-gray-700">
                  {periodEndDate}
                </span>
                .
              </p>
            )}

            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => navigate("/")}
                className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 transition-colors"
              >
                Go to dashboard
              </button>
              <button
                onClick={() => navigate("/billing")}
                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                View billing details
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
